import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useLocation } from 'react-router-dom'

const API_BASE_URL =
    'https://games.nlbsolucoes.com/api'

const IDLE_CONFIG_STORAGE_KEY =
    'games_idle_screen_config'

const IDLE_CONFIG_EVENT =
    'games:idle-config-updated'

const IDLE_PAUSE_EVENT =
    'games:idle-pause'

const IDLE_RESUME_EVENT =
    'games:idle-resume'

const PUBLIC_PATHS = [
    '/play/',
    '/p/',
]

const defaultConfig = {
    idleScreenEnabled: false,

    idleScreenDelay: 30000,

    idleScreenShowBackground: true,

    idleScreenBackgroundUrl: '',

    idleScreenShowVideo: false,

    idleScreenVideoUrl: '',

    idleScreenMessage:
        'Toque na tela para começar',

    idleScreenMessageColor:
        '#ffffff',

    idleScreenMessageFontSize:
        48,

    idleScreenMessagePosition:
        'CENTER',

    idleScreenOverlayColor:
        '#000000',

    idleScreenOverlayOpacity:
        20,
}

const getMediaUrl = (url) => {
    if (!url) {
        return null
    }

    if (
        url.startsWith('http://') ||
        url.startsWith('https://') ||
        url.startsWith('blob:') ||
        url.startsWith('data:')
    ) {
        return url
    }

    return `${API_BASE_URL}${url}`
}

const readStoredConfig = () => {
    try {
        const storedValue =
            sessionStorage.getItem(
                IDLE_CONFIG_STORAGE_KEY,
            )

        if (!storedValue) {
            return defaultConfig
        }

        const parsedValue =
            JSON.parse(storedValue)

        return {
            ...defaultConfig,
            ...parsedValue,

            idleScreenEnabled:
                parsedValue
                    .idleScreenEnabled ===
                true,

            idleScreenDelay:
                Number(
                    parsedValue
                        .idleScreenDelay ||
                    30000,
                ),

            idleScreenShowBackground:
                parsedValue
                    .idleScreenShowBackground !==
                false,

            idleScreenBackgroundUrl:
                parsedValue
                    .idleScreenBackgroundUrl ||
                '',

            idleScreenShowVideo:
                parsedValue
                    .idleScreenShowVideo ===
                true,

            idleScreenVideoUrl:
                parsedValue
                    .idleScreenVideoUrl ||
                '',

            idleScreenMessage:
                parsedValue
                    .idleScreenMessage ||
                'Toque na tela para começar',

            idleScreenMessageColor:
                parsedValue
                    .idleScreenMessageColor ||
                '#ffffff',

            idleScreenMessageFontSize:
                Number(
                    parsedValue
                        .idleScreenMessageFontSize ||
                    48,
                ),

            idleScreenMessagePosition:
                parsedValue
                    .idleScreenMessagePosition ||
                'CENTER',

            idleScreenOverlayColor:
                parsedValue
                    .idleScreenOverlayColor ||
                '#000000',

            idleScreenOverlayOpacity:
                Number(
                    parsedValue
                        .idleScreenOverlayOpacity ??
                    20,
                ),
        }
    } catch (error) {
        console.error(
            'Erro ao ler configuração da tela de espera:',
            error,
        )

        return defaultConfig
    }
}

const IdleScreenManager = () => {
    const location = useLocation()

    const timerRef =
        useRef(null)

    const pausedRef =
        useRef(false)

    const [config, setConfig] =
        useState(readStoredConfig)

    const [
        idleVisible,
        setIdleVisible,
    ] = useState(false)

    const isPublicRoute =
        PUBLIC_PATHS.some((path) =>
            location.pathname.startsWith(
                path,
            ),
        )

    const clearIdleTimer =
        useCallback(() => {
            if (!timerRef.current) {
                return
            }

            clearTimeout(
                timerRef.current,
            )

            timerRef.current = null
        }, [])

    const startIdleTimer =
        useCallback(() => {
            clearIdleTimer()

            if (
                !isPublicRoute ||
                config
                    .idleScreenEnabled !==
                true ||
                idleVisible ||
                pausedRef.current
            ) {
                return
            }

            const delay =
                Math.max(
                    1000,
                    Number(
                        config
                            .idleScreenDelay ||
                        30000,
                    ),
                )

            timerRef.current =
                window.setTimeout(() => {
                    timerRef.current = null

                    if (
                        pausedRef.current
                    ) {
                        return
                    }

                    setIdleVisible(true)
                }, delay)
        }, [
            clearIdleTimer,
            config.idleScreenDelay,
            config.idleScreenEnabled,
            idleVisible,
            isPublicRoute,
        ])

    const reloadConfig =
        useCallback(() => {
            const nextConfig =
                readStoredConfig()

            setConfig(nextConfig)

            if (
                nextConfig
                    .idleScreenEnabled !==
                true
            ) {
                clearIdleTimer()
                setIdleVisible(false)
            }
        }, [clearIdleTimer])

    /*
     * Atualiza as configurações quando
     * o jogo público é carregado.
     */
    useEffect(() => {
        window.addEventListener(
            IDLE_CONFIG_EVENT,
            reloadConfig,
        )

        return () => {
            window.removeEventListener(
                IDLE_CONFIG_EVENT,
                reloadConfig,
            )
        }
    }, [reloadConfig])

    /*
     * Recarrega as configurações ao
     * navegar entre a roleta e o resultado.
     */
    useEffect(() => {
        reloadConfig()
    }, [
        location.pathname,
        reloadConfig,
    ])

    /*
     * Pausa e retoma o cronômetro durante
     * ações que não devem ativar a tela de
     * espera, como o giro da roleta.
     */
    useEffect(() => {
        const handlePause = () => {
            pausedRef.current = true

            clearIdleTimer()
            setIdleVisible(false)
        }

        const handleResume = () => {
            pausedRef.current = false

            window.setTimeout(() => {
                startIdleTimer()
            }, 0)
        }

        window.addEventListener(
            IDLE_PAUSE_EVENT,
            handlePause,
        )

        window.addEventListener(
            IDLE_RESUME_EVENT,
            handleResume,
        )

        return () => {
            window.removeEventListener(
                IDLE_PAUSE_EVENT,
                handlePause,
            )

            window.removeEventListener(
                IDLE_RESUME_EVENT,
                handleResume,
            )
        }
    }, [
        clearIdleTimer,
        startIdleTimer,
    ])

    /*
     * Reinicia o cronômetro sempre que
     * houver atividade do usuário.
     */
    useEffect(() => {
        if (
            !isPublicRoute ||
            config
                .idleScreenEnabled !==
            true ||
            idleVisible ||
            pausedRef.current
        ) {
            clearIdleTimer()

            return undefined
        }

        const handleActivity = () => {
            if (
                pausedRef.current
            ) {
                return
            }

            startIdleTimer()
        }

        const activityEvents = [
            'pointerdown',
            'pointermove',
            'touchstart',
            'keydown',
            'wheel',
        ]

        activityEvents.forEach(
            (eventName) => {
                window.addEventListener(
                    eventName,
                    handleActivity,
                    {
                        passive: true,
                    },
                )
            },
        )

        startIdleTimer()

        return () => {
            clearIdleTimer()

            activityEvents.forEach(
                (eventName) => {
                    window.removeEventListener(
                        eventName,
                        handleActivity,
                    )
                },
            )
        }
    }, [
        clearIdleTimer,
        config.idleScreenEnabled,
        idleVisible,
        isPublicRoute,
        startIdleTimer,
    ])

    /*
     * Fecha a tela e limpa o cronômetro ao
     * sair das páginas públicas do jogo.
     */
    useEffect(() => {
        if (isPublicRoute) {
            return
        }

        pausedRef.current = false

        clearIdleTimer()
        setIdleVisible(false)
    }, [
        clearIdleTimer,
        isPublicRoute,
    ])

    /*
     * Fecha a tela de espera no primeiro
     * toque, clique ou tecla permitida.
     */
    const closeIdleScreen =
        useCallback(
            (event) => {
                if (event) {
                    event.preventDefault()
                    event.stopPropagation()
                }

                setIdleVisible(false)
            },
            [],
        )

    if (
        !isPublicRoute ||
        config
            .idleScreenEnabled !==
        true ||
        !idleVisible
    ) {
        return null
    }

    const backgroundUrl =
        config
            .idleScreenBackgroundUrl

    const videoUrl =
        config
            .idleScreenVideoUrl

    const shouldShowImage =
        config
            .idleScreenShowBackground ===
        true &&
        Boolean(backgroundUrl)

    const shouldShowVideo =
        !shouldShowImage &&
        config
            .idleScreenShowVideo ===
        true &&
        Boolean(videoUrl)

    const overlayOpacity =
        Math.min(
            100,
            Math.max(
                0,
                Number(
                    config
                        .idleScreenOverlayOpacity ??
                    20,
                ),
            ),
        ) / 100

    const messagePosition = String(
        config.idleScreenMessagePosition ||
        'CENTER',
    ).toUpperCase()

    const messageVerticalAlignment =
        messagePosition === 'TOP'
            ? 'flex-start'
            : messagePosition === 'BOTTOM'
                ? 'flex-end'
                : 'center'

    return (
        <div
            role="button"
            tabIndex={0}
            aria-label="Fechar tela de espera"
            onPointerDown={closeIdleScreen}
            onTouchStart={closeIdleScreen}
            onKeyDown={(event) => {
                if (
                    event.key === 'Enter' ||
                    event.key === ' ' ||
                    event.key === 'Escape'
                ) {
                    closeIdleScreen(event)
                }
            }}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 999999,

                width: '100vw',
                height: '100dvh',
                minHeight: '100vh',

                overflow: 'hidden',

                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent:
                    messageVerticalAlignment,

                background: '#050505',
                cursor: 'pointer',

                userSelect: 'none',
                WebkitUserSelect: 'none',
                touchAction: 'none',
            }}
        >
            {shouldShowImage && (
                <img
                    src={getMediaUrl(
                        backgroundUrl,
                    )}
                    alt=""
                    draggable={false}
                    style={{
                        position:
                            'absolute',

                        inset:
                            0,

                        width:
                            '100%',

                        height:
                            '100%',

                        objectFit:
                            'cover',

                        pointerEvents:
                            'none',

                        userSelect:
                            'none',
                    }}
                />
            )}

            {shouldShowVideo && (
                <video
                    src={getMediaUrl(
                        videoUrl,
                    )}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    disablePictureInPicture
                    controls={false}
                    style={{
                        position:
                            'absolute',

                        inset:
                            0,

                        width:
                            '100%',

                        height:
                            '100%',

                        objectFit:
                            'cover',

                        pointerEvents:
                            'none',
                    }}
                />
            )}

            <div
                style={{
                    position:
                        'absolute',

                    inset:
                        0,

                    background:
                        config
                            .idleScreenOverlayColor ||
                        '#000000',

                    opacity:
                        overlayOpacity,

                    pointerEvents:
                        'none',
                }}
            />

            <div
                style={{
                    position: 'relative',
                    zIndex: 2,

                    width: '100%',
                    maxWidth: 1200,

                    padding:
                        'clamp(24px, 6vw, 96px)',

                    boxSizing: 'border-box',

                    color:
                        config
                            .idleScreenMessageColor ||
                        '#ffffff',

                    fontSize: `clamp(24px, 5vw, ${Number(
                        config
                            .idleScreenMessageFontSize ||
                        48,
                    )}px)`,

                    fontWeight: 900,
                    lineHeight: 1.1,

                    textAlign: 'center',
                    whiteSpace: 'pre-line',
                    overflowWrap: 'break-word',

                    marginTop:
                        messagePosition === 'TOP'
                            ? '6vh'
                            : 0,

                    marginBottom:
                        messagePosition === 'BOTTOM'
                            ? '6vh'
                            : 0,

                    textShadow:
                        '0 4px 18px rgba(0,0,0,.70)',

                    pointerEvents: 'none',
                }}
            >
                {config.idleScreenMessage ||
                    'Toque na tela para começar'}
            </div>
        </div>
    )
}

export {
    IDLE_CONFIG_EVENT,
    IDLE_CONFIG_STORAGE_KEY,
    IDLE_PAUSE_EVENT,
    IDLE_RESUME_EVENT,
}

export default IdleScreenManager